import React, { createContext, useState, useContext, useEffect } from 'react';
import apiClient, { API_BASE_URL } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [otpRequired, setOtpRequired] = useState(false);
  const [otpUserId, setOtpUserId] = useState(null);
  const [error, setError] = useState(null);

  // Check for existing session on app load
  useEffect(() => {
    // Check if user is already logged in via token in localStorage
    const token = localStorage.getItem('accessToken');
    const userData = localStorage.getItem('userData');
    
    if (token && userData) {
      try {
        setCurrentUser(JSON.parse(userData));
      } catch (err) {
        console.error('Error parsing user data from localStorage', err);
        // Clear invalid data
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('userData');
      }
    }
    
    setLoading(false);
  }, []);

  // Login function
  const login = async (email, password) => {
    setError(null);
    try {
      const response = await apiClient.post('/accounts/login/', {
        email,
        password
      });
      
      const data = response.data;
      
      
      if (data.two_factor_enabled) {
        // User has 2FA enabled, we need OTP verification
        setOtpRequired(true);
        setOtpUserId(data.user_id);
        return { requiresOtp: true, userId: data.user_id };
      } else {
        // User doesn't have 2FA, proceed with login
        localStorage.setItem('accessToken', data.access);
        localStorage.setItem('refreshToken', data.refresh);
        
        // Fetch user info
        const userInfo = await fetchUserProfile(data.access);
        setCurrentUser(userInfo);
        localStorage.setItem('userData', JSON.stringify(userInfo));
        
        return { success: true };
      }
    } catch (err) {
      setError(err.message || 'Login failed');
      throw err;
    }
  };

  // Verify OTP function for 2FA
  const verifyOtp = async (otp) => {
    setError(null);
    try {
      const response = await apiClient.post('/accounts/verify-otp/', {
        otp,
        user_id: otpUserId
      });
      
      const data = response.data;
      
      // Store tokens
      localStorage.setItem('accessToken', data.access);
      localStorage.setItem('refreshToken', data.refresh);
      
      // Fetch user info
      const userInfo = await fetchUserProfile(data.access);
      setCurrentUser(userInfo);
      localStorage.setItem('userData', JSON.stringify(userInfo));
      
      // Reset OTP state
      setOtpRequired(false);
      setOtpUserId(null);
      
      return { success: true };
    } catch (err) {
      setError(err.message || 'OTP verification failed');
      throw err;
    }
  };

  // Fetch user profile
  const fetchUserProfile = async (token) => {
    try {
      // The axios interceptor will automatically add the token to headers
      const response = await apiClient.get('/accounts/profile/');
      return response.data;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw new Error('Failed to fetch user profile');
    }
  };

  // Refresh token function
  const refreshToken = async () => {
    try {
      const refresh = localStorage.getItem('refreshToken');
      
      if (!refresh) {
        logout();
        return false;
      }
      
      const response = await apiClient.post('/accounts/token/refresh/', { refresh });
      
      localStorage.setItem('accessToken', response.data.access);
      return true;
    } catch (err) {
      console.error('Token refresh error:', err);
      logout();
      return false;
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userData');
    setCurrentUser(null);
  };

  const value = {
    currentUser,
    loading,
    otpRequired,
    otpUserId,
    error,
    login,
    logout,
    verifyOtp,
    refreshToken,
    fetchUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
